import { create } from 'zustand';

import {
    sendDrivers,
    sendDriversTrip,
    sendDriverUpsert,
    sendDriverRemove,
    sendDriversSelectedTrips
} from '../channels/driversChannel';

export const NO_DRIVER_ID = 'NO_DRIVER';

export const NO_DRIVER_OPTION = {
    id: NO_DRIVER_ID,
    label: 'Sem Motorista'
};

// =========================
// HELPERS
// =========================

function getDriverId(driver) {

    return String(
        driver?.CODFUNC || ''
    ).trim();

}

function sortDrivers(drivers = []) {

    return [...drivers].sort((a, b) =>
        String(a.NOMEFUNC || '').localeCompare(
            String(b.NOMEFUNC || ''),
            'pt-BR'
        )
    );

}

function buildDriversState(drivers = []) {

    const sorted = sortDrivers(drivers);

    return {
        driversDisponiveis: sorted,

        driversDisponiveisMap: new Map(
            sorted.map(driver => [
                getDriverId(driver),
                driver
            ])
        ),

        driversComOpcaoSemMotorista: [
            NO_DRIVER_OPTION,
            ...sorted
        ]
    };

}

function buildAllowDrivers(driversSelecionados) {

    return driversSelecionados === null
        ? null
        : new Set(
            (driversSelecionados || [])
                .map(String)
        );

}

function buildAllowDriversEmViagem(driversSelecionados) {

    return driversSelecionados === null
        ? null
        : new Set(
            (driversSelecionados || [])
                .map(String)
        );

}

// =========================
// LOCAL STORAGE
// =========================

function getDriversSelecionados() {

    try {

        const saved =
            localStorage.getItem(
                'driversSelecionados'
            );

        return saved
            ? JSON.parse(saved)
            : null;

    } catch {

        return null;

    }

}

function saveDriversSelecionados(driversSelecionados) {

    try {

        if (driversSelecionados === null) {

            localStorage.removeItem(
                'driversSelecionados'
            );

            return;

        }

        localStorage.setItem(
            'driversSelecionados',
            JSON.stringify(driversSelecionados)
        );

    } catch {}

}

function getDriversEmViagemSelecionados() {

    try {

        const saved =
            localStorage.getItem(
                'driversEmViagemSelecionados'
            );

        return saved
            ? JSON.parse(saved)
            : null;

    } catch {

        return null;

    }

}

function saveDriversEmViagemSelecionados(driversSelecionados) {

    try {

        if (driversSelecionados === null) {

            localStorage.removeItem(
                'driversEmViagemSelecionados'
            );

            return;

        }

        localStorage.setItem(
            'driversEmViagemSelecionados',
            JSON.stringify(driversSelecionados)
        );

    } catch {}

}

// =========================
// INITIAL STATE
// =========================

const initialDriversSelecionados =
    getDriversSelecionados();

const initialDriversEmViagemSelecionados =
    getDriversEmViagemSelecionados();

// =========================
// STORE
// =========================

export const useDriversStore = create((set) => ({

    // =========================
    // DISPONÍVEIS
    // =========================

    driversDisponiveis: [],
    driversDisponiveisMap: new Map(),

    driversComOpcaoSemMotorista: [NO_DRIVER_OPTION],

    driversSelecionados:
        initialDriversSelecionados,

    allowDrivers:
        buildAllowDrivers(
            initialDriversSelecionados
        ),

    // =========================
    // EM VIAGEM
    // =========================

    driversEmViagem: [],
    driversEmViagemMap: new Map(),

    driversEmViagemSelecionados:
        initialDriversEmViagemSelecionados,

    allowDriversEmViagem:
        buildAllowDriversEmViagem(
            initialDriversEmViagemSelecionados
        ),

    // =========================
    // SELECIONADOS
    // =========================

    setDriversSelecionados: (driversSelecionados) => {

        saveDriversSelecionados(
            driversSelecionados
        );

        set({
            driversSelecionados,

            allowDrivers:
                buildAllowDrivers(
                    driversSelecionados
                )
        });

    },

    // =========================
    // SELECIONADOS EM VIAGEM
    // =========================

    setDriversEmViagemSelecionados: (
        driversSelecionados,
        broadcast = true
    ) => {

        set(state => {

            let selecionados =
                driversSelecionados;

            if (
                selecionados !== null &&
                selecionados.length ===
                    state.driversEmViagem.length
            ) {
                selecionados = null;
            }

            saveDriversEmViagemSelecionados(
                selecionados
            );

            if (broadcast) {

                sendDriversSelectedTrips(
                    driversSelecionados
                );

            }

            return {
                driversEmViagemSelecionados:
                    selecionados,

                allowDriversEmViagem:
                    buildAllowDriversEmViagem(
                        selecionados
                    )
            };

        });

    },

    // =========================
    // SET DRIVERS
    // =========================

    setDrivers: (
        drivers = [],
        broadcast = true
    ) => {

        set(
            buildDriversState(drivers)
        );

        if (broadcast) {
            sendDrivers(drivers);
        }

    },

    // =========================
    // UPSERT
    // =========================

    upsertDriver: (
        driver,
        broadcast = true
    ) => {

        const driverId =
            getDriverId(driver);

        if (!driverId) {
            return;
        }

        set(state => {

            const map =
                new Map(
                    state.driversDisponiveisMap
                );

            map.set(
                driverId,
                driver
            );

            if (broadcast) {
                sendDriverUpsert(driver);
            }

            return buildDriversState(
                Array.from(map.values())
            );

        });

    },

    // =========================
    // REMOVE
    // =========================

    removeDriver: (
        driverId,
        broadcast = true
    ) => {

        const normalizedDriverId =
            String(driverId || '').trim();

        if (!normalizedDriverId) {
            return;
        }

        set(state => {

            const map =
                new Map(
                    state.driversDisponiveisMap
                );

            map.delete(
                normalizedDriverId
            );

            if (broadcast) {
                sendDriverRemove(
                    normalizedDriverId
                );
            }

            return buildDriversState(
                Array.from(map.values())
            );

        });

    },

    // =========================
    // DRIVERS EM VIAGEM
    // =========================

    updateDriversEmViagem: (
        trips = [],
        broadcast = true
    ) => {

        set(state => {

            const driversEmViagem = [];
            const driversEmViagemMap = new Map();

            for (const trip of trips) {

                const driverId = String(
                    trip?.driver_id || NO_DRIVER_ID
                ).trim();

                if (
                    driversEmViagemMap.has(driverId)
                ) {
                    continue;
                }

                // =========================
                // SEM MOTORISTA
                // =========================

                if (driverId === NO_DRIVER_ID) {

                    driversEmViagem.push(
                        NO_DRIVER_OPTION
                    );

                    driversEmViagemMap.set(
                        NO_DRIVER_ID,
                        NO_DRIVER_OPTION
                    );

                    continue;
                }

                // =========================
                // MOTORISTA
                // =========================

                const driver =
                    state.driversDisponiveisMap.get(
                        driverId
                    );

                if (!driver) {
                    continue;
                }

                const driverOption = {
                    ...driver,
                    id: driverId,
                    label: String(
                        driver.NOMEFUNC || ''
                    ).trim()
                };

                driversEmViagem.push(
                    driverOption
                );

                driversEmViagemMap.set(
                    driverId,
                    driverOption
                );

            }

            let selecionados =
                state.driversEmViagemSelecionados;

            if (selecionados !== null) {

                selecionados =
                    selecionados.filter(id =>
                        driversEmViagemMap.has(
                            String(id)
                        )
                    );

            }

            return {
                driversEmViagem,
                driversEmViagemMap,

                driversEmViagemSelecionados:
                    selecionados,

                allowDriversEmViagem:
                    buildAllowDriversEmViagem(
                        selecionados
                    )
            };

        });

        if (broadcast) {
            sendDriversTrip(trips);
        }

    },

    // =========================
    // RESET
    // =========================

    resetDrivers: () =>
        set({
            driversDisponiveis: [],
            driversDisponiveisMap: new Map(),

            driversComOpcaoSemMotorista: [
                NO_DRIVER_OPTION
            ],

            driversSelecionados: null,
            allowDrivers: null,

            driversEmViagem: [],
            driversEmViagemMap: new Map(),

            driversEmViagemSelecionados: null,
            allowDriversEmViagem: null
        })

}));